import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useVIPStore = defineStore('vip', () => {
  const vipLevels = [
    {
      id: 'bronze',
      name: 'Bronze',
      minPoints: 0,
      maxPoints: 999,
      color: '#CD7F32',
      icon: '🥉',
      benefits: [
        'Access to all games',
        'Standard withdrawal limits',
        'Email support',
        'Birthday bonus'
      ]
    },
    {
      id: 'silver',
      name: 'Silver',
      minPoints: 1000,
      maxPoints: 4999,
      color: '#C0C0C0',
      icon: '🥈',
      benefits: [
        'All Bronze benefits',
        '5% weekly cashback',
        'Priority withdrawals',
        'Exclusive promotions',
        'Live chat support'
      ]
    },
    {
      id: 'gold',
      name: 'Gold',
      minPoints: 5000,
      maxPoints: 14999,
      color: '#FFD700',
      icon: '🥇',
      benefits: [
        'All Silver benefits',
        '10% weekly cashback',
        'Higher withdrawal limits',
        'Personal account manager',
        'Faster withdrawals (4h)',
        'Exclusive tournaments'
      ]
    },
    {
      id: 'platinum',
      name: 'Platinum',
      minPoints: 15000,
      maxPoints: 49999,
      color: '#E5E4E2',
      icon: '💎',
      benefits: [
        'All Gold benefits',
        '15% weekly cashback',
        'VIP withdrawal limits',
        '24/7 priority support',
        'Custom bonuses',
        'Exclusive events invitations'
      ]
    },
    {
      id: 'diamond',
      name: 'Diamond',
      minPoints: 50000,
      maxPoints: Infinity,
      color: '#00CED1',
      icon: '👑',
      benefits: [
        'All Platinum benefits',
        '20% weekly cashback',
        'Instant withdrawals',
        'Dedicated VIP host',
        'Luxury gifts',
        'Personalized rewards',
        'Exclusive VIP events'
      ]
    }
  ]

  const currentPoints = ref(750)
  const totalWagered = ref(1250)
  const nextLevelPoints = ref(1000)
  const isLoading = ref(false)
  const claimedRewards = ref([])

  const initVIP = () => {
    try {
      const stored = localStorage.getItem('vip_data')
      if (stored) {
        const data = JSON.parse(stored)
        currentPoints.value = data.points || 0
        totalWagered.value = data.wagered || 0
        claimedRewards.value = data.claimed || []
      }
    } catch (e) {
      console.error('Error loading VIP data:', e)
    }
  }

  const saveVIP = () => {
    try {
      localStorage.setItem('vip_data', JSON.stringify({
        points: currentPoints.value,
        wagered: totalWagered.value,
        claimed: claimedRewards.value
      }))
    } catch (e) {
      console.error('Error saving VIP data:', e)
    }
  }

  const currentLevel = computed(() => {
    for (let i = vipLevels.length - 1; i >= 0; i--) {
      if (currentPoints.value >= vipLevels[i].minPoints) {
        return vipLevels[i]
      }
    }
    return vipLevels[0]
  })

  const nextLevel = computed(() => {
    const currentIndex = vipLevels.findIndex(l => l.id === currentLevel.value.id)
    return vipLevels[currentIndex + 1] || null
  })

  const progressPercent = computed(() => {
    if (!nextLevel.value) return 100
    const range = nextLevel.value.minPoints - currentLevel.value.minPoints
    const progress = currentPoints.value - currentLevel.value.minPoints
    return Math.min(100, Math.max(0, (progress / range) * 100))
  })

  const pointsToNextLevel = computed(() => {
    if (!nextLevel.value) return 0
    return Math.max(0, nextLevel.value.minPoints - currentPoints.value)
  })

  const allLevels = computed(() => vipLevels)

  const addPoints = (points) => {
    currentPoints.value += points
    saveVIP()
  }

  const addWagered = (amount) => {
    totalWagered.value += amount
    const points = Math.floor(amount / 10)
    if (points > 0) {
      addPoints(points)
    }
    saveVIP()
  }

  const claimReward = (rewardId) => {
    if (!claimedRewards.value.includes(rewardId)) {
      claimedRewards.value.push(rewardId)
      saveVIP()
      return true
    }
    return false
  }

  const hasClaimedReward = (rewardId) => {
    return claimedRewards.value.includes(rewardId)
  }

  const getWeeklyCashback = () => {
    const cashbackRates = {
      'bronze': 0,
      'silver': 0.05,
      'gold': 0.10,
      'platinum': 0.15,
      'diamond': 0.20
    }
    return cashbackRates[currentLevel.value.id] || 0
  }

  const getWithdrawalLimit = () => {
    const limits = {
      'bronze': 5000,
      'silver': 10000,
      'gold': 25000,
      'platinum': 50000,
      'diamond': 100000
    }
    return limits[currentLevel.value.id] || 5000
  }

  initVIP()

  return {
    currentPoints,
    totalWagered,
    isLoading,
    claimedRewards,
    currentLevel,
    nextLevel,
    progressPercent,
    pointsToNextLevel,
    allLevels,
    addPoints,
    addWagered,
    claimReward,
    hasClaimedReward,
    getWeeklyCashback,
    getWithdrawalLimit,
    saveVIP
  }
})
